import React, { useState, useEffect, useCallback } from 'react';
import { Html } from '@react-three/drei';
import { tokens } from '@fluentui/react-components';

const MeasureTooltip = ({ label }) => (
    <div style={{
        position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
        transform: 'translateX(-50%)', background: '#1e293b', color: '#f8fafc',
        fontSize: 10, fontWeight: 500, padding: '3px 8px', borderRadius: 5,
        whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 9999,
        boxShadow: '0 4px 10px rgba(0,0,0,0.25)', fontFamily: 'sans-serif',
    }}>
        {label}
        <div style={{
            position: 'absolute', top: '100%', left: '50%',
            transform: 'translateX(-50%)', width: 0, height: 0,
            borderLeft: '4px solid transparent', borderRight: '4px solid transparent',
            borderTop: '4px solid #1e293b',
        }} />
    </div>
);

const Measurements = ({
    value,
    position,
    onChange,
    label = "cm",
    disabled = false,
    modalOpen = false,
    rotation = [0, 0, 0],
    hideUnit = false,
    title = '',
    useTransform = false,
    htmlStyle = {},
    bgColor = '#6b7280',
    textColor = '#ffffff',
    borderColor = 'none',
}) => {
    const formatValueToCm = (val) => (val * 100).toFixed(1);

    const [localCmVal, setLocalCmVal] = useState(formatValueToCm(value));
    const [editing, setEditing] = useState(false);
    const [placeholder, setPlaceholder] = useState('');
    const [hovered, setHovered] = useState(false);

    useEffect(() => {
        if (!editing) {
            setLocalCmVal(formatValueToCm(value));
        }
    }, [value, editing]);

    const handleBlur = () => {
        setEditing(false);
        const numCmVal = parseFloat(localCmVal);
        if (!isNaN(numCmVal) && numCmVal > 0) {
            onChange(numCmVal / 100); 
        } else {
            setLocalCmVal(formatValueToCm(value));
            if (numCmVal === 0) {
                onChange(0);
            }
        }
        setPlaceholder('');
    };

    const handleChange = (e) => {
        const val = e.target.value;
        if (val === '' || /^\d*\.?\d*$/.test(val)) {
            setLocalCmVal(val);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        }
    };

    const handleInputFocus = useCallback((e) => {
        if (e.target) {
            e.target.select();
        }
    }, []);

    const handleWrapperClick = useCallback(() => {
        if (!disabled && !modalOpen) {
            setEditing(true);
            setPlaceholder(localCmVal);
        }
    }, [disabled, modalOpen, localCmVal]);

    return (
        <group position={position} rotation={rotation}>
            <Html center transform={useTransform}>
                <div style={{ position: 'relative', display: 'inline-flex', ...htmlStyle }}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                >
                <div
                    onPointerDown={(e) => e.stopPropagation()}
                    style={{
                        pointerEvents: disabled || modalOpen ? 'none' : 'auto',
                        cursor: disabled || modalOpen ? 'default' : 'pointer',
                        backgroundColor: bgColor,
                        borderRadius: 3,
                        border: borderColor === 'none' ? 'none' : `1px solid ${borderColor}`,
                        outline: 'none',
                        padding: '1px 3px',
                        minWidth: 35,
                        height: 18,
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        userSelect: 'none',
                    }}
                    onClick={handleWrapperClick}
                >
                    {editing ? (
                        <input
                            value={localCmVal}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            onFocus={handleInputFocus}
                            autoFocus
                            placeholder={placeholder}
                            className="fitit-measure-input"
                            style={{
                                width: 35,
                                border: 'none',
                                outline: 'none',
                                background: 'transparent',
                                textAlign: 'center',
                                fontSize: 11,
                                color: textColor,
                                padding: 0,
                            }}
                        />
                    ) : (
                        <span style={{ fontSize: '11px', fontWeight: tokens.fontWeightMedium, whiteSpace: 'nowrap', color: textColor }}>
                            {formatValueToCm(value)}{!hideUnit && label}
                        </span>
                    )}
                </div>
                {hovered && title && <MeasureTooltip label={title} />}
                </div>
            </Html>
        </group>
    );
};

export default Measurements;
